import React, { useState, useEffect } from 'react';
import { useAuth } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { ScrollArea } from '../components/ui/scroll-area';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Bell, Send, Users, User, AlertCircle, Info, Loader2, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';

const NotificationsPage = () => {
  const { api, user } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [formData, setFormData] = useState({
    title: '',
    message: '',
    type: 'general',
    priority: 'normal',
    recipient_type: 'all'
  });

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const response = await api().get('/users');
      setUsers(response.data);
    } catch (error) {
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.title.trim()) {
      toast.error('Please enter a title');
      return;
    }
    if (!formData.message.trim()) {
      toast.error('Please enter a message');
      return;
    }
    if (formData.recipient_type === 'specific' && selectedUsers.length === 0) {
      toast.error('Please select at least one recipient');
      return;
    }

    setSending(true);
    try {
      await api().post('/notifications', {
        ...formData,
        recipient_ids: formData.recipient_type === 'specific' ? selectedUsers : []
      });
      toast.success('Notification sent successfully!');
      setFormData({
        title: '',
        message: '',
        type: 'general',
        priority: 'normal',
        recipient_type: 'all'
      });
      setSelectedUsers([]);
    } catch (error) {
      toast.error('Failed to send notification');
    } finally {
      setSending(false);
    }
  };

  const toggleUserSelection = (userId) => {
    setSelectedUsers(prev => 
      prev.includes(userId) 
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  const selectAllUsers = () => {
    if (selectedUsers.length === users.length) {
      setSelectedUsers([]);
    } else {
      setSelectedUsers(users.map(u => u.id));
    }
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'alert': return <AlertCircle className="w-4 h-4 text-red-500" />;
      case 'system': return <Info className="w-4 h-4 text-amber-500" />;
      default: return <Bell className="w-4 h-4 text-primary" />;
    }
  };

  const renderUserList = () => {
    const items = [];
    for (let i = 0; i < users.length; i++) {
      const u = users[i];
      const isSelected = selectedUsers.includes(u.id);
      items.push(
        <div
          key={u.id}
          className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
            isSelected ? 'border-primary bg-primary/5' : 'border-border hover:bg-muted/50'
          }`}
          onClick={() => toggleUserSelection(u.id)}
          data-testid={`user-select-${u.id}`}
        >
          <Checkbox checked={isSelected} />
          <div className="flex-1 min-w-0">
            <p className="font-medium text-sm">{u.name}</p>
            <p className="text-xs text-muted-foreground truncate">{u.email}</p>
          </div>
          {u.roles && u.roles.length > 0 && (
            <Badge variant="outline" className="text-xs">{u.roles[0]?.name}</Badge>
          )}
        </div>
      );
    }
    return items;
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <Bell className="w-6 h-6" />
          Notification Center
        </h1>
        <p className="text-muted-foreground">Send notifications to your team members</p>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Send Notification Form */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Send className="w-5 h-5" />
              Send Notification
            </CardTitle>
            <CardDescription>
              Create and send notifications to your team
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="title">Title *</Label>
                <Input
                  id="title"
                  placeholder="Notification title"
                  value={formData.title}
                  onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                  data-testid="notification-title-input"
                />
              </div>

              <div>
                <Label htmlFor="message">Message *</Label>
                <Textarea
                  id="message"
                  placeholder="Enter your message..."
                  value={formData.message}
                  onChange={(e) => setFormData(prev => ({ ...prev, message: e.target.value }))}
                  rows={4}
                  data-testid="notification-message-input"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="type">Type</Label>
                  <Select
                    value={formData.type}
                    onValueChange={(value) => setFormData(prev => ({ ...prev, type: value }))}
                  >
                    <SelectTrigger data-testid="notification-type-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="general">
                        <span className="flex items-center gap-2">
                          <Bell className="w-4 h-4" /> General
                        </span>
                      </SelectItem>
                      <SelectItem value="alert">
                        <span className="flex items-center gap-2">
                          <AlertCircle className="w-4 h-4 text-red-500" /> Alert
                        </span>
                      </SelectItem>
                      <SelectItem value="system">
                        <span className="flex items-center gap-2">
                          <Info className="w-4 h-4 text-amber-500" /> System
                        </span>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="priority">Priority</Label>
                  <Select
                    value={formData.priority}
                    onValueChange={(value) => setFormData(prev => ({ ...prev, priority: value }))}
                  >
                    <SelectTrigger data-testid="notification-priority-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="low">Low</SelectItem>
                      <SelectItem value="normal">Normal</SelectItem>
                      <SelectItem value="high">High</SelectItem>
                      <SelectItem value="urgent">Urgent</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <Label>Recipients</Label>
                <Tabs 
                  value={formData.recipient_type} 
                  onValueChange={(value) => setFormData(prev => ({ ...prev, recipient_type: value }))}
                  className="mt-2"
                >
                  <TabsList className="w-full">
                    <TabsTrigger value="all" className="flex-1" data-testid="recipient-all-tab">
                      <Users className="w-4 h-4 mr-2" />
                      All Team
                    </TabsTrigger>
                    <TabsTrigger value="specific" className="flex-1" data-testid="recipient-specific-tab">
                      <User className="w-4 h-4 mr-2" />
                      Specific Users
                    </TabsTrigger>
                  </TabsList>

                  <TabsContent value="all" className="mt-3">
                    <div className="p-3 bg-muted/50 rounded-lg text-center text-sm text-muted-foreground">
                      <Users className="w-8 h-8 mx-auto mb-2 opacity-50" />
                      Notification will be sent to all {users.length} team members
                    </div>
                  </TabsContent>

                  <TabsContent value="specific" className="mt-3">
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-muted-foreground">
                          {selectedUsers.length} of {users.length} selected
                        </span>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={selectAllUsers}
                        >
                          {selectedUsers.length === users.length ? 'Deselect All' : 'Select All'}
                        </Button>
                      </div>
                      <ScrollArea className="h-[200px] rounded-lg border">
                        <div className="p-2 space-y-2">
                          {loading ? (
                            <div className="text-center py-4 text-muted-foreground">Loading users...</div>
                          ) : (
                            renderUserList()
                          )}
                        </div>
                      </ScrollArea>
                    </div>
                  </TabsContent>
                </Tabs>
              </div>

              <Button type="submit" className="w-full" disabled={sending} data-testid="send-notification-btn">
                {sending ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <Send className="w-4 h-4 mr-2" />
                )}
                Send Notification
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Preview Card */}
        <Card>
          <CardHeader>
            <CardTitle>Preview</CardTitle>
            <CardDescription>
              How your notification will appear
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="p-4 rounded-lg border bg-card">
              <div className="flex gap-3">
                <div className="flex-shrink-0 mt-0.5">
                  {getTypeIcon(formData.type)}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2">
                    <p className="font-medium text-sm">
                      {formData.title || 'Notification Title'}
                    </p>
                    {formData.priority === 'urgent' && (
                      <Badge variant="destructive" className="text-xs">Urgent</Badge>
                    )}
                    {formData.priority === 'high' && (
                      <Badge variant="secondary" className="text-xs bg-orange-100 text-orange-700">High</Badge>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    {formData.message || 'Your notification message will appear here...'}
                  </p>
                  <div className="flex items-center gap-2 mt-2">
                    <span className="text-xs text-muted-foreground">From: {user?.name}</span>
                    <span className="text-xs text-muted-foreground">• Just now</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-6 space-y-3">
              <h4 className="font-medium text-sm">Notification Tips</h4>
              <div className="space-y-2 text-sm text-muted-foreground">
                <div className="flex items-start gap-2">
                  <CheckCircle2 className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
                  <span>Keep titles concise and actionable</span>
                </div>
                <div className="flex items-start gap-2">
                  <CheckCircle2 className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
                  <span>Use urgent priority sparingly for critical matters</span>
                </div>
                <div className="flex items-start gap-2">
                  <CheckCircle2 className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
                  <span>Include relevant details in the message body</span>
                </div>
                <div className="flex items-start gap-2">
                  <CheckCircle2 className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
                  <span>Target specific users when not everyone needs to know</span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default NotificationsPage;
